import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Plus, Trash2, Edit, ShoppingBag, Package, Eye, EyeOff, ArrowLeft, Radio, Lock } from "lucide-react";
import { toast } from "sonner";
import MarketingUpgradeCard from "@/components/MarketingUpgradeCard";

const money = (c) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format((c || 0) / 100);

// Shop manager — works for businesses (with Marketing add-on), verified
// influencers, and approved band owners. Backend enforces the gates.
export default function ShopManage() {
  const [params] = useSearchParams();
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("products");
  const [editing, setEditing] = useState(null);  // product being edited (null = closed)
  const [showNew, setShowNew] = useState(params.get("new") === "1");
  const [config, setConfig] = useState({ eligible: null, owner_type: null, shop_enabled: true, reason: "" });
  const [toggling, setToggling] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [cfg, pr, or] = await Promise.all([
        api.get("/me/shop/config").catch(() => ({ data: { eligible: false, reason: "Sign in required" } })),
        api.get("/shop/products/mine").catch(() => ({ data: { items: [] } })),
        api.get("/shop/orders/received").catch(() => ({ data: { items: [] } })),
      ]);
      setConfig(cfg.data || {});
      setItems(pr.data?.items || []);
      setOrders(or.data?.items || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const toggleShop = async (next) => {
    setToggling(true);
    try {
      await api.post("/me/shop/toggle", { enabled: !!next });
      setConfig((c) => ({ ...c, shop_enabled: !!next }));
      toast.success(next ? "Shop is now live" : "Shop hidden from customers");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setToggling(false); }
  };

  const deleteProduct = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    try {
      await api.delete(`/shop/products/${id}`);
      toast("Deleted");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };

  const toggleActive = async (p) => {
    try {
      await api.patch(`/shop/products/${p.id}`, { is_active: !p.is_active });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };

  return (
    <div className="max-w-5xl mx-auto px-6 md:px-10 py-8 pb-20" data-testid="shop-manage-page">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div>
          <Link to={`/shop/${user?.id}`} className="inline-flex items-center gap-1 text-sm text-[#B31E1E] hover:underline mb-2">
            <ArrowLeft size={14} /> View my shop
          </Link>
          <h1 className="font-display text-4xl tracking-tighter">Manage shop</h1>
          {config.owner_type && (
            <p className="text-xs font-mono uppercase tracking-wider text-gray-500 mt-1">
              {config.owner_type === "business" ? "Business shop" : config.owner_type === "influencer" ? "Influencer shop" : "Band shop"}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {/* Enable / disable toggle — hides the shop from strangers when off */}
          {config.eligible && (
            <div className="inline-flex items-center gap-2 rounded-full border border-border bg-white px-3 py-1.5">
              <Switch
                checked={config.shop_enabled}
                onCheckedChange={toggleShop}
                disabled={toggling}
                data-testid="shop-enabled-toggle"
              />
              <span className="text-xs font-mono uppercase tracking-wider text-gray-600">{config.shop_enabled ? "Shop ON" : "Shop OFF"}</span>
            </div>
          )}
          {/* TikTok-Shop-style — go live with all products pre-attached */}
          {config.eligible && items.length > 0 && (
            <Link to="/marketplace/live/new?shop=1">
              <Button variant="outline" className="rounded-[35px]" data-testid="shop-go-live-button">
                <Radio size={13} className="mr-1.5 text-red-600" /> Go live from shop
              </Button>
            </Link>
          )}
          <Button
            onClick={() => setShowNew(true)}
            disabled={!config.eligible}
            className="rounded-[35px] bg-[#B31E1E] hover:bg-[#8F1717] shadow-sm"
            data-testid="shop-new-product-button"
          >
            <Plus size={14} className="mr-1.5" /> New product
          </Button>
        </div>
      </div>

      {/* Eligibility gate — shows when the caller cannot run a shop */}
      {config.eligible === false && (
        <div className="mb-6 space-y-4" data-testid="shop-gate-block">
          <Card className="rounded-md p-5 bg-amber-50 border-amber-200 flex items-start gap-3" data-testid="shop-gate-banner">
            <Lock size={18} className="text-amber-700 mt-0.5 shrink-0" />
            <div className="text-sm text-amber-900 flex-1">
              <div className="font-semibold">Shop not available on this account yet.</div>
              <p className="mt-1 text-amber-800">{config.reason || "Shops require a Business, verified Influencer, or approved Band account — plus the Marketing add-on ($35/mo)."}</p>
            </div>
          </Card>
          {/* Direct-to-Stripe upgrade — one click when the caller is otherwise
              eligible (business / approved influencer / approved band) but
              hasn't purchased the Marketing add-on yet. */}
          {(user?.role === "business" || user?.is_influencer || config.owner_type === "band") && (
            <MarketingUpgradeCard active={!!config.marketing_active} />
          )}
        </div>
      )}

      <div className="inline-flex rounded-sm border border-border overflow-hidden mb-4">
        {[
          { k: "products", label: `Products · ${items.length}` },
          { k: "orders",   label: `Orders · ${orders.length}` },
        ].map((t) => (
          <button
            key={t.k}
            onClick={() => setTab(t.k)}
            className={`px-3 py-1.5 text-xs font-mono uppercase tracking-wider border-l first:border-l-0 border-border ${tab === t.k ? "bg-[#B31E1E] text-white" : "bg-white text-gray-700 hover:bg-gray-50"}`}
            data-testid={`shop-manage-tab-${t.k}`}
          >{t.label}</button>
        ))}
      </div>

      {loading ? (
        <p className="text-[#8B7B73]">Loading…</p>
      ) : tab === "products" ? (
        items.length === 0 ? (
          <Card className="rounded-md p-10 text-center border border-dashed" data-testid="shop-manage-empty">
            <ShoppingBag size={30} className="mx-auto text-gray-400 mb-2" />
            <p className="text-sm text-gray-600">No products yet.</p>
            <Button onClick={() => setShowNew(true)} className="mt-4 rounded-[35px] bg-[#B31E1E] hover:bg-[#8F1717]">Add first product</Button>
          </Card>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {items.map((p) => (
              <Card key={p.id} className="rounded-md overflow-hidden flex" data-testid={`shop-manage-row-${p.id}`}>
                <div className="w-24 h-24 shrink-0 bg-gray-100">
                  {p.photos?.[0] ? <img src={p.photos[0]} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-gray-400"><ShoppingBag size={22} /></div>}
                </div>
                <div className="p-3 flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="font-medium truncate">{p.title}</p>
                    {!p.is_active && <Badge className="rounded-sm bg-gray-200 text-gray-700 hover:bg-gray-200 text-[10px]">Hidden</Badge>}
                  </div>
                  <p className="text-xs text-[#5C4E48]">{money(p.price_cents)}{p.inventory_qty !== null && ` · ${p.inventory_qty} in stock`}</p>
                  <div className="mt-1.5 flex gap-1">
                    <button onClick={() => setEditing(p)} className="text-xs text-[#B31E1E] hover:underline inline-flex items-center gap-1" data-testid={`shop-edit-${p.id}`}>
                      <Edit size={11} /> Edit
                    </button>
                    <span className="text-gray-300">·</span>
                    <button onClick={() => toggleActive(p)} className="text-xs text-[#5C4E48] hover:text-[#B31E1E] inline-flex items-center gap-1" data-testid={`shop-toggle-${p.id}`}>
                      {p.is_active ? <><EyeOff size={11} /> Hide</> : <><Eye size={11} /> Show</>}
                    </button>
                    <span className="text-gray-300">·</span>
                    <button onClick={() => deleteProduct(p.id)} className="text-xs text-red-600 hover:underline inline-flex items-center gap-1" data-testid={`shop-delete-${p.id}`}>
                      <Trash2 size={11} /> Delete
                    </button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )
      ) : (
        orders.length === 0 ? (
          <Card className="rounded-md p-10 text-center border border-dashed" data-testid="shop-orders-empty">
            <Package size={30} className="mx-auto text-gray-400 mb-2" />
            <p className="text-sm text-gray-600">No orders yet.</p>
          </Card>
        ) : (
          <div className="space-y-2">
            {orders.map((o) => (
              <Card key={o.id} className="rounded-md p-4 flex items-center gap-3" data-testid={`shop-order-${o.id}`}>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-mono text-gray-500">
                    {new Date(o.created_at).toLocaleString()} · {o.status}
                  </p>
                  <p className="font-medium mt-0.5">
                    {o.qty} × {o.product_title}
                    {o.size && ` · ${o.size}`}{o.color && ` · ${o.color}`}
                  </p>
                  <p className="text-xs text-[#5C4E48] mt-0.5">{o.buyer_name} · {o.buyer_email}</p>
                </div>
                <p className="font-black font-mono">{money(o.total_amount_cents)}</p>
              </Card>
            ))}
          </div>
        )
      )}

      {/* Product edit/new dialog */}
      {(showNew || editing) && (
        <ProductForm
          initial={editing}
          onClose={() => { setShowNew(false); setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

// ------------- Product form dialog ----------------------------------
function ProductForm({ initial, onClose }) {
  const isNew = !initial;
  const [form, setForm] = useState({
    title: initial?.title || "",
    description: initial?.description || "",
    price_dollars: initial ? ((initial.price_cents || 0) / 100).toFixed(2) : "",
    inventory_qty: initial?.inventory_qty ?? "",
    photos: initial?.photos || [],
    sizes: (initial?.sizes || []).join(", "),
    colors: (initial?.colors || []).join(", "),
    category: initial?.category || "",
  });
  const [saving, setSaving] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const addPhotoFromUrl = () => {
    const url = window.prompt("Paste an image URL (jpg/png):");
    if (!url) return;
    setForm({ ...form, photos: [...form.photos, url] });
  };

  const removePhoto = (i) => {
    setForm({ ...form, photos: form.photos.filter((_, idx) => idx !== i) });
  };

  const save = async () => {
    const price_cents = Math.max(0, Math.round(parseFloat(form.price_dollars || "0") * 100));
    if (!form.title || price_cents <= 0) {
      toast.error("Title and price are required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        title: form.title.trim(),
        description: form.description.trim(),
        price_cents,
        inventory_qty: form.inventory_qty === "" ? null : Math.max(0, parseInt(form.inventory_qty, 10)),
        photos: form.photos,
        sizes: form.sizes.split(",").map((s) => s.trim()).filter(Boolean),
        colors: form.colors.split(",").map((c) => c.trim()).filter(Boolean),
        category: form.category.trim(),
      };
      if (isNew) {
        await api.post("/shop/products", payload);
        toast.success("Product created");
      } else {
        await api.patch(`/shop/products/${initial.id}`, payload);
        toast.success("Saved");
      }
      onClose();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={() => onClose()}>
      <DialogContent className="rounded-sm max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="product-form-dialog">
        <DialogHeader>
          <DialogTitle>{isNew ? "New product" : "Edit product"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Title</Label>
            <Input value={form.title} onChange={update("title")} className="rounded-sm mt-1" placeholder="Blue Ridge Handmade Mug" data-testid="product-form-title" />
          </div>
          <div>
            <Label>Description</Label>
            <Textarea rows={3} value={form.description} onChange={update("description")} className="rounded-sm mt-1" placeholder="Materials, sizing, care instructions…" data-testid="product-form-description" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Price (USD)</Label>
              <Input type="number" step="0.01" min={0} value={form.price_dollars} onChange={update("price_dollars")} className="rounded-sm mt-1 font-mono" placeholder="25.00" data-testid="product-form-price" />
            </div>
            <div>
              <Label>Stock (blank = unlimited)</Label>
              <Input type="number" min={0} value={form.inventory_qty} onChange={update("inventory_qty")} className="rounded-sm mt-1 font-mono" placeholder="20" data-testid="product-form-stock" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Sizes (comma separated)</Label>
              <Input value={form.sizes} onChange={update("sizes")} className="rounded-sm mt-1" placeholder="S, M, L, XL" data-testid="product-form-sizes" />
            </div>
            <div>
              <Label>Colors (comma separated)</Label>
              <Input value={form.colors} onChange={update("colors")} className="rounded-sm mt-1" placeholder="Black, White" data-testid="product-form-colors" />
            </div>
          </div>
          <div>
            <Label>Category</Label>
            <Input value={form.category} onChange={update("category")} className="rounded-sm mt-1" placeholder="Apparel, Home, Food…" data-testid="product-form-category" />
          </div>
          <div>
            <div className="flex items-center justify-between">
              <Label>Photos</Label>
              <Button size="sm" variant="outline" className="rounded-sm h-7" onClick={addPhotoFromUrl} data-testid="product-form-add-photo">
                <Plus size={11} className="mr-1" /> Add photo URL
              </Button>
            </div>
            {form.photos.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-2">
                {form.photos.map((ph, i) => (
                  <div key={i} className="relative w-20 h-20 rounded-sm overflow-hidden border border-[#EAE3DE]">
                    <img src={ph} alt="" className="w-full h-full object-cover" />
                    <button
                      onClick={() => removePhoto(i)}
                      className="absolute top-0.5 right-0.5 bg-red-600 text-white rounded-full w-5 h-5 text-xs flex items-center justify-center hover:bg-red-700"
                      type="button"
                    >×</button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-sm">Cancel</Button>
          <Button onClick={save} disabled={saving} className="rounded-sm bg-[#B31E1E] hover:bg-[#8F1717] text-white" data-testid="product-form-save">
            {saving ? "Saving…" : "Save product"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
